import { css } from '@emotion/react';
import { ColDef, IAggFuncParams, ICellRendererParams } from 'ag-grid-community';
import { Typography } from '@mui/material';
import { AgFormatterService } from '../../../../util/ag-formatter-service';
import { FMT } from '../../../../util/formatter-service';
import { IEquityLot } from './equity-holdings-model';

const formatLotNumber = (id: 'integer' | 'currencyShort') =>
  AgFormatterService.get().getFormatterForId<IEquityLot, number>(id);

const fundCell = css`
  align-items: center;
  display: flex;
  gap: 0.375rem;
  min-width: 0;
`;

/** The design's "+2" pill, marking a group that spans more funds than the one named. */
const overflowChip = css`
  background: var(--mui-palette-action-hover);
  border-radius: 4px;
  color: var(--mui-palette-text-secondary);
  flex-shrink: 0;
  font-size: 0.6875rem;
  line-height: 1.4;
  padding: 0 0.25rem;
`;

/** The name anchors the row, semibold against the chips' small caps, as the design draws it. */
const securityName = css`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const truncated = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const lotCount = css`
  color: var(--mui-palette-text-secondary);
  margin-left: 0.375rem;
`;

/** Name of the custom aggregation registered on the grid, collecting a group's distinct funds. */
export const DistinctFundsAggFunc = 'distinctFunds';

/**
 * A group's Fund cell names one fund and counts the rest, so `sum`-style aggregation is no use —
 * this collects the distinct ids instead. Values arriving from a nested group are already arrays.
 */
export function distinctFundsAggFunc(params: IAggFuncParams<IEquityLot, number | number[]>): number[] {
  const ids = new Set<number>();
  for (const value of params.values) {
    if (Array.isArray(value)) {
      value.forEach((id) => ids.add(id));
    } else if (typeof value === 'number') {
      ids.add(value);
    }
  }
  return [...ids];
}

/** Shared with Security History, which renders the same Fund column. */
export function FundCell({ value, node }: ICellRendererParams<IEquityLot, number | number[]>) {
  // The pinned Total row spans every fund, so the design leaves its Fund cell empty.
  if (node.rowPinned) {
    return null;
  }
  const fundIds = Array.isArray(value) ? value : value == null ? [] : [value];
  if (fundIds.length === 0) {
    return null;
  }

  return (
    <div css={fundCell}>
      <span css={truncated}>{FMT.format('fund', fundIds[0])}</span>
      {fundIds.length > 1 && <span css={overflowChip}>{`+${fundIds.length - 1}`}</span>}
    </div>
  );
}

/**
 * Everything renders through the auto group column, so the name comes from three places: the Total
 * label, the row's own path segment, or — when that segment is the opaque lot id used to keep the
 * funds of one buying block distinct — the security itself.
 *
 * A fund's slice repeats its security's name rather than naming the fund, as the design draws it: the
 * Fund column already says which fund, and a row that names only a fund stops being self-describing.
 *
 * A block bought by a single fund is a leaf whose segment is still the block's label. So "is this a
 * group?" is the wrong question; "is my segment the lot id?" is the right one.
 */
function SecurityCell(params: ICellRendererParams<IEquityLot>) {
  const { node, value, data } = params;

  if (node.rowPinned) {
    return (
      <Typography variant='body2' fontWeight={600}>
        {'Total'}
      </Typography>
    );
  }

  const segment = value == null ? '' : String(value);
  const isOpaqueLotSegment = data != null && segment === data.id;
  const label = isOpaqueLotSegment ? data.securityName : segment;
  // `allLeafChildren`, not `allChildrenCount`: for tree data AG Grid counts "all children, groups and
  // leafs" in the latter, so a type holding one security of two lots would read "(3 lots)".
  const leafCount = node.group ? (node.allLeafChildren?.length ?? 0) : 0;

  return (
    <span css={securityName}>
      {label}
      {leafCount > 1 && <span css={lotCount}>{`(${leafCount} lots)`}</span>}
    </span>
  );
}

export function getSecurityOverviewAutoGroupColDef(): ColDef<IEquityLot> {
  return {
    headerName: 'Security',
    // Kept in view while the rest of the columns scroll horizontally, per the design.
    pinned: 'left',
    minWidth: 320,
    flex: 2,
    cellRendererParams: {
      innerRenderer: SecurityCell,
      // The count is rendered inside the cell as "(N lots)" instead.
      suppressCount: true,
    },
  };
}

export function getSecurityOverviewColDefs(): ColDef<IEquityLot>[] {
  return [
    {
      headerName: 'Fund',
      colId: 'fundId',
      minWidth: 130,
      valueGetter: ({ data }) => data?.fundId ?? null,
      aggFunc: DistinctFundsAggFunc,
      cellRenderer: FundCell,
      filter: true,
      // Filtering and grouping compare fund names, not the ids the cell renders from.
      filterValueGetter: ({ data }) => (data?.fundId == null ? null : FMT.format('fund', data.fundId)),
    },
    {
      headerName: 'Shares',
      field: 'shares',
      type: 'numericColumn',
      minWidth: 110,
      aggFunc: 'sum',
      valueFormatter: formatLotNumber('integer'),
    },
    {
      headerName: 'Invested',
      field: 'invested',
      type: 'numericColumn',
      minWidth: 110,
      aggFunc: 'sum',
      valueFormatter: formatLotNumber('currencyShort'),
    },
    {
      headerName: 'FMV',
      field: 'fmv',
      type: 'numericColumn',
      minWidth: 110,
      aggFunc: 'sum',
      valueFormatter: formatLotNumber('currencyShort'),
    },
  ];
}
